import type { Box, Point, SceneShape } from '../model.js';
import { box, point, roundGeometry, type SceneBuilder } from '../scene-builder.js';

/**
 * The shared construction kit for the general-primitive spike.
 *
 * Every primitive in this folder draws through these helpers so the nine
 * subject-agnostic shapes read as one hand alongside the shipping lesson
 * families: one type scale, one header block, one leader grammar, one
 * measurement grammar. Nothing here knows a subject, and nothing here accepts
 * an authored coordinate — callers pass semantics and measured extents only.
 */

/** Locked type scale. Nothing in this folder may emit a size below MICRO. */
export const TYPE = {
  micro: 11,
  annotation: 12,
  label: 13,
  primary: 14,
  heading: 16,
  value: 13,
  taught: 22,
} as const;

export type DensityTier = 'compact' | 'phone' | 'wide';

export interface Frame {
  tier: DensityTier;
  width: number;
  height: number;
  margin: number;
  /** First baseline of the content band, below the shared header block. */
  top: number;
  /** Last usable baseline before the figure edge. */
  bottom: number;
  contentWidth: number;
}

export function frameFor(width: number, height: number): Frame {
  const tier: DensityTier = width <= 340 ? 'compact' : width <= 450 ? 'phone' : 'wide';
  const margin = tier === 'compact' ? 16 : tier === 'phone' ? 18 : 24;
  return {
    tier,
    width,
    height,
    margin,
    top: 74,
    bottom: height - 18,
    contentWidth: width - margin * 2,
  };
}

/**
 * Conservative advance widths. These deliberately over-estimate both faces so a
 * layout computed here always survives the renderer and the containment tests,
 * which measure with narrower advances.
 */
export function textWidth(text: string, size: number, mono = false, uppercase = false): number {
  const advance = mono ? 0.62 : 0.52;
  return text.length * (size * advance + (uppercase ? 1.15 : 0));
}

/** Largest size at or below `preferred` (never below TYPE.micro) that fits `available`. */
export function fittedSize(text: string, preferred: number, available: number, mono = false, uppercase = false): number {
  for (let size = preferred; size > TYPE.micro; size -= 0.5) {
    if (textWidth(text, size, mono, uppercase) <= available) return size;
  }
  return TYPE.micro;
}

export function fits(text: string, size: number, available: number, mono = false, uppercase = false): boolean {
  return textWidth(text, size, mono, uppercase) <= available;
}

/**
 * Greedy word wrap with no ellipsis anywhere and no line budget: a caller that
 * cannot fit its text must give it more room or pick a different density tier,
 * never truncate a taught label. Callers size their band from `lines.length`.
 */
export function wrapText(text: string, size: number, available: number, mono = false): string[] {
  const words = text.trim().replace(/\s+/g, ' ').split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (textWidth(next, size, mono) <= available || !current) {
      current = next;
      continue;
    }
    lines.push(current);
    current = word;
  }
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

/**
 * The shared header: a mono kicker naming the relation, then one factual line.
 * `summary` may be a list of candidates from longest to shortest; the first one
 * that fits the tier at full size wins, so narrow widths lose words, never size.
 */
export function header(builder: SceneBuilder, frame: Frame, semanticId: string, kicker: string, summary: string | string[]): void {
  builder.text(`${semanticId}.eyebrow`, semanticId, 'annotation', point(frame.margin, 26), kicker, {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  const candidates = Array.isArray(summary) ? summary : [summary];
  const chosen = candidates.find((candidate) => fits(candidate, TYPE.annotation, frame.contentWidth, true)) ?? candidates[candidates.length - 1]!;
  builder.text(`${semanticId}.summary`, semanticId, 'structure', point(frame.margin, 48), chosen, {
    size: fittedSize(chosen, TYPE.annotation, frame.contentWidth, true),
    mono: true,
    weight: 700,
  });
  builder.anchor(`${semanticId}.header`, semanticId, point(frame.margin, 52), 'down');
}

/** A footer note in soft ink, used for scale statements and honest caveats. */
export function note(builder: SceneBuilder, id: string, semanticId: string, at: Point, text: string, align: 'start' | 'middle' | 'end' = 'start'): void {
  builder.text(id, semanticId, 'annotation', at, text, {
    size: TYPE.micro,
    mono: true,
    weight: 500,
    align,
  });
}

/**
 * A dimension: extension lines from the measured feature, an arrow-terminated
 * measure line, and a unit-bearing label centred on it.
 */
export function dimensionH(
  builder: SceneBuilder,
  semanticId: string,
  x1: number,
  x2: number,
  y: number,
  featureY: number,
  label: string,
  options: { size?: number; above?: boolean } = {},
): void {
  const size = options.size ?? TYPE.micro;
  const above = options.above ?? true;
  builder.line(`${semanticId}.extension.start`, semanticId, 'construction', point(x1, featureY), point(x1, y + (above ? -4 : 4)));
  builder.line(`${semanticId}.extension.end`, semanticId, 'construction', point(x2, featureY), point(x2, y + (above ? -4 : 4)));
  builder.line(`${semanticId}.measure`, semanticId, 'dimension', point(x1, y), point(x2, y), { markerStart: true, markerEnd: true });
  builder.text(`${semanticId}.label`, semanticId, 'annotation', point((x1 + x2) / 2, y + (above ? -7 : 15)), label, {
    size,
    mono: true,
    weight: 700,
    align: 'middle',
  });
  builder.anchor(`${semanticId}.start`, semanticId, point(x1, y), 'left');
  builder.anchor(`${semanticId}.end`, semanticId, point(x2, y), 'right');
  builder.hit(semanticId, box(Math.min(x1, x2) - 6, y - 12, Math.abs(x2 - x1) + 12, 24));
}

export function dimensionV(
  builder: SceneBuilder,
  semanticId: string,
  y1: number,
  y2: number,
  x: number,
  featureX: number,
  label: string,
  options: { size?: number } = {},
): void {
  const size = options.size ?? TYPE.micro;
  builder.line(`${semanticId}.extension.start`, semanticId, 'construction', point(featureX, y1), point(x - 4, y1));
  builder.line(`${semanticId}.extension.end`, semanticId, 'construction', point(featureX, y2), point(x - 4, y2));
  builder.line(`${semanticId}.measure`, semanticId, 'dimension', point(x, y1), point(x, y2), { markerStart: true, markerEnd: true });
  builder.text(`${semanticId}.label`, semanticId, 'annotation', point(x - 7, (y1 + y2) / 2), label, {
    size,
    mono: true,
    weight: 700,
    align: 'middle',
    rotate: -90,
  });
  builder.anchor(`${semanticId}.start`, semanticId, point(x, y1), 'up');
  builder.anchor(`${semanticId}.end`, semanticId, point(x, y2), 'down');
  builder.hit(semanticId, box(x - 14, Math.min(y1, y2) - 6, 28, Math.abs(y2 - y1) + 12));
}

/**
 * Displaces a column of labels so none overlap, without moving the feature each
 * one names. Purely a function of the requested positions, so it is stable.
 */
export function displaceColumn(preferred: number[], minimumGap: number, low: number, high: number): number[] {
  const order = preferred.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value || a.index - b.index);
  let cursor = low;
  for (const entry of order) {
    entry.value = Math.max(entry.value, cursor);
    cursor = entry.value + minimumGap;
  }
  let overflow = cursor - minimumGap - high;
  if (overflow > 0) {
    for (let index = order.length - 1; index >= 0; index -= 1) {
      const limit = high - (order.length - 1 - index) * minimumGap;
      if (order[index]!.value > limit) order[index]!.value = limit;
    }
  }
  const result = new Array<number>(preferred.length);
  for (const entry of order) result[entry.index] = roundGeometry(entry.value);
  return result;
}

/** An annotation leader: evidence to displaced label, elbowed, never causal. */
export function leader(builder: SceneBuilder, id: string, semanticId: string, from: Point, to: Point, kneeX: number): void {
  builder.path(id, semanticId, 'annotation', `M ${d(from.x)} ${d(from.y)} L ${d(kneeX)} ${d(from.y)} L ${d(kneeX)} ${d(to.y)} L ${d(to.x)} ${d(to.y)}`);
}

/** A 6 px status dot: the only way a primitive may state a verdict with colour. */
export function statusDot(builder: SceneBuilder, id: string, semanticId: string, at: Point, role: 'active' | 'positive' | 'negative' | 'surface'): void {
  builder.rect(id, semanticId, role, box(at.x - 3, at.y - 3, 6, 6), { radius: 3 });
}

/** A value chip: mono ink on a semantic tint, never coloured text. */
export function valueChip(
  builder: SceneBuilder,
  semanticId: string,
  idPrefix: string,
  at: Point,
  text: string,
  options: { role?: 'slot' | 'header' | 'active' | 'surface'; size?: number; align?: 'start' | 'middle' | 'end' } = {},
): Box {
  const size = options.size ?? TYPE.micro;
  const width = textWidth(text, size, true) + 14;
  const height = size + 10;
  const align = options.align ?? 'start';
  const x = align === 'start' ? at.x : align === 'middle' ? at.x - width / 2 : at.x - width;
  const bounds = box(x, at.y - height / 2, width, height);
  builder.rect(`${idPrefix}.chip`, semanticId, options.role ?? 'slot', bounds, { radius: 4 });
  builder.text(`${idPrefix}.chip.text`, semanticId, 'structure', point(x + width / 2, at.y + size * 0.36), text, {
    size,
    mono: true,
    weight: 700,
    align: 'middle',
  });
  return bounds;
}

export function expand(bounds: Box, padding: number): Box {
  return box(bounds.x - padding, bounds.y - padding, bounds.width + padding * 2, bounds.height + padding * 2);
}

/** Clamps a box back inside the viewport so a hit region can never escape. */
export function clampBox(bounds: Box, width: number, height: number): Box {
  const x = Math.max(0, Math.min(bounds.x, width));
  const y = Math.max(0, Math.min(bounds.y, height));
  return box(x, y, Math.min(bounds.width, width - x), Math.min(bounds.height, height - y));
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Arc segment of a circle centred on `centre`, drawn clockwise from a1 to a2. */
export function arcPath(centre: Point, radius: number, a1: number, a2: number): string {
  const start = point(centre.x + Math.cos(a1) * radius, centre.y + Math.sin(a1) * radius);
  const end = point(centre.x + Math.cos(a2) * radius, centre.y + Math.sin(a2) * radius);
  const large = Math.abs(a2 - a1) > Math.PI ? 1 : 0;
  return `M ${d(start.x)} ${d(start.y)} A ${d(radius)} ${d(radius)} 0 ${large} 1 ${d(end.x)} ${d(end.y)}`;
}

/**
 * Half-disc shell as a closed polygon: the renderer fills polygons and leaves
 * paths unfilled, and a section needs its material to read as material. The
 * segment count is fixed so the geometry is identical on every compile.
 */
export function shellPolygon(centre: Point, outer: number, inner: number, segments = 48): Point[] {
  const points: Point[] = [];
  for (let step = 0; step <= segments; step += 1) {
    const angle = -Math.PI / 2 + (step / segments) * Math.PI;
    points.push(point(centre.x + Math.cos(angle) * outer, centre.y + Math.sin(angle) * outer));
  }
  if (inner <= 0) {
    points.push(point(centre.x, centre.y + outer));
    return points;
  }
  for (let step = segments; step >= 0; step -= 1) {
    const angle = -Math.PI / 2 + (step / segments) * Math.PI;
    points.push(point(centre.x + Math.cos(angle) * inner, centre.y + Math.sin(angle) * inner));
  }
  return points;
}

/** Path coordinate rounding: identical quantum to the scene builder. */
export function d(value: number): number {
  return roundGeometry(value);
}

export function shapeIsText(shape: SceneShape): shape is Extract<SceneShape, { kind: 'text' }> {
  return shape.kind === 'text';
}

/** Formats a number without inventing precision. */
export function num(value: number, decimals = 0): string {
  if (Number.isInteger(value) && decimals === 0) return String(value);
  return value.toFixed(decimals);
}
